import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  signInWithPopup,
  deleteUser
} from 'firebase/auth'
import { doc, setDoc, getDoc, collection, query, where, getDocs, limit, serverTimestamp, deleteDoc } from 'firebase/firestore'
import { auth, db, googleProvider } from '../config/firebase'

interface UserProfile {
  email: string
  username: string
  name?: string
  createdAt: Date
  avatarSeed?: string
  avatarStyle?: string
  avatarCustomization?: any
  settings?: {
    theme: string
    language: string
    emailAlerts: boolean
  }
  subscriptionPlan?: 'free' | 'premium'
  subscriptionStatus?: 'active' | 'expired' | 'canceled'
  country?: string
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  login: (emailOrUsername: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  register: (email: string, password: string, username: string) => Promise<void>
  logout: () => Promise<void>
  refreshUserProfile: () => Promise<void>
  updateUserLocation: (country: string) => Promise<void>
  deleteAccount: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

// Helper function to check if input is email
const isEmail = (input: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(input)
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  const loadUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId))
      if (userDoc.exists()) {
        const data = userDoc.data()
        // Normalizar avatarCustomization: solo asignar si tiene propiedades válidas
        const avatarCustomization = data.avatarCustomization && 
                                   typeof data.avatarCustomization === 'object' &&
                                   Object.keys(data.avatarCustomization).length > 0
                                   ? data.avatarCustomization
                                   : undefined;
        
        return {
          email: data.email || '',
          username: data.username || '',
          name: data.name || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          avatarSeed: data.avatarSeed || '',
          avatarStyle: data.avatarStyle || 'avataaars',
          avatarCustomization: avatarCustomization,
          settings: data.settings || {
            theme: 'system',
            language: 'es',
            emailAlerts: true
          },
          subscriptionPlan: data.subscriptionPlan || 'free',
          subscriptionStatus: data.subscriptionStatus,
          country: data.country
        }
      }
      return null
    } catch (error) {
      console.error('Error loading user profile:', error)
      return null
    }
  }

  const refreshUserProfile = async () => {
    if (user?.uid) {
      const profile = await loadUserProfile(user.uid)
      setUserProfile(profile)
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if user profile exists in Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        if (!userDoc.exists()) {
          // Create user profile if it doesn't exist
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            username: user.displayName || '',
            createdAt: serverTimestamp(),
            settings: {
              theme: 'system',
              language: 'es',
              emailAlerts: true
            },
            subscriptionPlan: 'free'
          })
        }

        // Load complete user profile
        const profile = await loadUserProfile(user.uid)
        setUserProfile(profile)
      } else {
        setUserProfile(null)
      }
      setUser(user)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const login = async (emailOrUsername: string, password: string) => {
    try {
      let email = emailOrUsername

      // If input is not an email, assume it's a username and look up the email
      if (!isEmail(emailOrUsername)) {
        // Search for user by username in Firestore
        const usersRef = collection(db, 'users')
        const q = query(
          usersRef,
          where('username', '==', emailOrUsername),
          limit(1)
        )

        const querySnapshot = await getDocs(q)

        if (querySnapshot.empty) {
          throw new Error('Usuario no encontrado')
        }

        const userData = querySnapshot.docs[0].data()
        email = userData.email
      }

      // Sign in with Firebase Auth using email
      await signInWithEmailAndPassword(auth, email, password)
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      // Check if user profile exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid))

      if (!userDoc.exists()) {
        // Create user profile if it doesn't exist
        // Generate a unique username from email
        const emailUsername = user.email?.split('@')[0] || 'user'
        let username = emailUsername
        let counter = 1

        // Check if username already exists and generate unique one
        while (true) {
          const usersRef = collection(db, 'users')
          const q = query(
            usersRef,
            where('username', '==', username),
            limit(1)
          )

          const querySnapshot = await getDocs(q)

          if (querySnapshot.empty) {
            break
          }

          username = `${emailUsername}${counter}`
          counter++
        }

        // Create user document in Firestore
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          username: username,
          name: user.displayName,
          createdAt: serverTimestamp(),
          avatarSeed: username, // Use username as seed for consistent avatar
          settings: {
            theme: 'system',
            language: 'es',
            emailAlerts: true
          },
          subscriptionPlan: 'free'
        })

        // Initialize empty collections for the user
        const collections = ['medications', 'history', 'doctors', 'email-recipients']

        for (const collectionName of collections) {
          await setDoc(doc(db, 'users', user.uid, collectionName, '_placeholder'), {
            placeholder: true,
            createdAt: serverTimestamp()
          })
        }
      }

      // Load/refresh user profile
      await refreshUserProfile()

    } catch (error) {
      console.error('Google login error:', error)
      throw error
    }
  }

  const register = async (email: string, password: string, username: string) => {
    try {
      // Check if username already exists
      const usersRef = collection(db, 'users')
      const q = query(
        usersRef,
        where('username', '==', username),
        limit(1)
      )

      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        throw new Error('El nombre de usuario ya existe')
      }

      // Create user with Firebase Auth
      const { user } = await createUserWithEmailAndPassword(auth, email, password)

      // Update user profile with display name
      await updateProfile(user, {
        displayName: username
      })

      // Create user document in Firestore with the same structure as original app
      await setDoc(doc(db, 'users', user.uid), {
        email: email,
        username: username,
        createdAt: serverTimestamp(),
        settings: {
          theme: 'system',
          language: 'es',
          emailAlerts: true
        },
        subscriptionPlan: 'free'
      })

      // Initialize empty collections for the user (like in original app)
      const collections = ['medications', 'history', 'doctors', 'email-recipients']

      for (const collectionName of collections) {
        await setDoc(doc(db, 'users', user.uid, collectionName, '_placeholder'), {
          placeholder: true,
          createdAt: serverTimestamp()
        })
      }

    } catch (error) {
      console.error('Register error:', error)
      throw error
    }
  }

  const logout = async () => {
    try {
      await signOut(auth)
    } catch (error) {
      console.error('Logout error:', error)
      throw error
    }
  }

  const updateUserLocation = async (country: string) => {
    if (!user) return

    try {
      await setDoc(doc(db, 'users', user.uid), {
        country: country
      }, { merge: true })

      await refreshUserProfile()
    } catch (error) {
      console.error('Error updating user location:', error)
      throw error
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    refreshUserProfile,
    updateUserLocation,
    deleteAccount: async () => {
      if (!user) return

      try {
        // Delete user data from Firestore
        // Note: This is a shallow delete. Subcollections need to be deleted manually if not handled by extensions/cloud functions
        // For this app we will try to delete known subcollections
        const collections = ['medications', 'history', 'doctors', 'email-recipients', 'care-cases']

        for (const collectionName of collections) {
          try {
            const subCollectionRef = collection(db, 'users', user.uid, collectionName)
            const snapshot = await getDocs(subCollectionRef)
            const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref))
            await Promise.all(deletePromises)
          } catch (e) {
            console.error(`Error deleting subcollection ${collectionName}:`, e)
          }
        }

        // Delete main user document
        await deleteDoc(doc(db, 'users', user.uid))

        // Delete user from Auth
        await deleteUser(user)
      } catch (error: any) {
        console.error('Error deleting account:', error)
        if (error.code === 'auth/requires-recent-login') {
          // This is a common error when deleting account
          throw new Error('security-reauth-needed')
        }
        throw error
      }
    }
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 